import React, {
  FC,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useLocation } from "react-router-dom";
import classNames from "classnames";
import { Compose } from "@natera/platform/lib/components/compose";

import {
  BloodDrawProvider,
  FetalSexProvider,
  MfaSetupProvider,
  MpxProvider,
  OrderRenewalProvider,
  PasswordPolicyProvider,
  ProfileProvider,
  TestCardProvider,
  TestCardUtilsProvider,
  TestHistoryProvider,
  UserContext,
  ViewResultProvider,
} from "@app/provider";
import { PreBillContextWrapper } from "@app/preBill/preBillWrapper";
import { PostBillContextWrapper } from "@app/postBill/postBillWrapper";
import { UPSContextWrapper } from "@app/ups/upsWrapper";
import { GradientWrapper, Header, Navigation } from "@app/components";
import MobileNavigation from "@app/components/mobileNavigation";
import BillingMenuProvider from "@app/components/navigation/billingMenuContext";
import { STAGE } from "@app/service/user";

import useIsMobileDimension from "../../../hooks/useIsMobileDimension";
import "./main.scss";
import { routes } from "@app/routing";
import { trimTrailingSlashes } from "@app/utils/common";

const TABLET_MIN_WIDTH = 746;

const Main: FC<{ children: ReactNode }> = ({ children }) => {
  const location = useLocation();
  const pathname = trimTrailingSlashes(location.pathname);
  const { uppUser, loadProfile } = useContext(UserContext);
  const isMobile = useIsMobileDimension();
  const [isDesktopViewport, setIsDesktopViewport] = useState(
    () => typeof window !== "undefined" && window.innerWidth >= TABLET_MIN_WIDTH
  );

  useEffect(() => {
    const desktopViewportQuery = window.matchMedia(
      `(min-width: ${TABLET_MIN_WIDTH}px)`
    );
    const handler = (e: MediaQueryListEvent) => setIsDesktopViewport(e.matches);
    setIsDesktopViewport(desktopViewportQuery.matches);
    desktopViewportQuery.addEventListener("change", handler);
    return () => desktopViewportQuery.removeEventListener("change", handler);
  }, []);

  const wrapWithBackgroundWrapper =
    [routes.setupProfile, routes.confirmation]
      .map(trimTrailingSlashes)
      .includes(pathname) ||
    (trimTrailingSlashes(routes.home) === pathname &&
      uppUser?.stage !== STAGE.FILLED);

  useEffect(() => {
    loadProfile();
  }, []);

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  // Tracks whether the *next* transform change on the mobile drawer should
  // animate. Flipped on by `toggleMenu` (an actual user click), and flipped
  // off during viewport resize so the drawer never slides in/out just because
  // the user is dragging a window across the mobile breakpoint. See
  // navigation.scss — the .navigation--animate class is what carries the
  // transition rule.
  const [animateMenu, setAnimateMenu] = useState(false);

  const toggleMenu = () => {
    setAnimateMenu(true);
    setIsMenuOpen((isMenuOpen) => !isMenuOpen);
  };

  useEffect(() => {
    // Any resize event kills the animate flag before CSS gets a chance to
    // transition, so cross-breakpoint changes snap without a slide. The flag
    // gets re-set the next time the user actually clicks the hamburger.
    const handleResize = () => setAnimateMenu(false);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Lock page scroll while the mobile drawer is open — the drawer sits above
  // the content but does not clip the page beneath, so without this the user
  // can still scroll the body behind the overlay (feels broken on iOS Safari
  // in particular). Only applies on mobile; desktop nav stays inline and
  // never needs a lock. Restores whatever `overflow` value was on <body>
  // before we touched it, in case anyone else has set it.
  useEffect(() => {
    if (!isMobile || !isMenuOpen) return;

    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, [isMobile, isMenuOpen]);

  const isMenuVisible = useMemo(() => {
    if (uppUser?.stage) {
      return ![
        STAGE.BLANK,
        STAGE.CONFIRMATION,
        STAGE.DATE_OF_BIRTH_VERIFICATION,
        STAGE.PROFILE_SETUP,
        STAGE.MFA_SETUP,
        STAGE.COMMUNICATION_PREFERENCES,
      ].includes(uppUser.stage);
    }
  }, [uppUser]);

  useEffect(() => {
    return () => setIsMenuOpen(false);
  }, [location]);

  const renderPrivateMain = () => (
    <main className="private-main">
      {isMenuVisible && (
        <Navigation
          isMenuOpen={isMenuOpen}
          toggleMenu={toggleMenu}
          animateMenu={animateMenu}
        />
      )}
      <div
        className={classNames("private-content", {
          "private-content--menu-visible": !isMobile && isMenuVisible,
        })}
      >
        <PreBillContextWrapper>
          <Compose
            components={[
              ProfileProvider,
              MfaSetupProvider,
              PasswordPolicyProvider,
              TestCardUtilsProvider,
              TestCardProvider,
              BloodDrawProvider,
              FetalSexProvider,
              ViewResultProvider,
              TestHistoryProvider,
              OrderRenewalProvider,
              MpxProvider,
              UPSContextWrapper,
              PostBillContextWrapper,
            ]}
          >
            {children}
          </Compose>
        </PreBillContextWrapper>
      </div>
      {isMenuVisible && isMobile && <MobileNavigation />}
    </main>
  );

  const hideDesktopHeader = isDesktopViewport && isMenuVisible;

  return (
    <BillingMenuProvider>
      {!hideDesktopHeader && (
        <Header
          toggleMenu={toggleMenu}
          isBurgerVisible={isMenuVisible}
          isMenuOpen={isMenuOpen}
        />
      )}
      {wrapWithBackgroundWrapper ? (
        <GradientWrapper
          className="private-main-background"
          display
          disableForMobile
        >
          {renderPrivateMain()}
        </GradientWrapper>
      ) : (
        renderPrivateMain()
      )}
    </BillingMenuProvider>
  );
};

export default Main;
